import styled from '@emotion/styled';
import React, { useEffect, useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Text, Divider, IconButton } from '../components/atoms';
import { AvatarName, PostForm } from '../components/molecules';
import { AnswerItem } from '../components/organisms';
import { Modal } from '../components/templates';
import { usePostContext } from '../contexts/PostContext';
import { useUserContext } from '../contexts/UserContext';
import theme from '../styles/theme';

const QuestionerContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${theme.spacing.l};
  color: ${theme.colors.gray600};
  padding: 0 2px;
`;

const PostContainer = styled.section`
  border-radius: ${theme.surface.borderRadius};
  padding: 30px 60px;
  box-shadow: ${theme.surface.boxShadow};
  margin-top: ${theme.spacing.m};

  ${theme.mediaQuery('tablet')} {
    padding: 30px ${theme.spacing.m};
  }
`;

const Question = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 220px;
  padding: 0 20px;
  box-sizing: border-box;
  border-radius: 12px;
  margin-bottom: 16px;
  overflow: hidden;
`;

const Answers = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: ${theme.spacing.l};
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${theme.spacing.xs};
`;

const PostDetail = () => {
  const { postId } = useParams();
  const {
    user: { id: userId },
  } = useUserContext();
  const {
    selectedPost,
    handleSetSelectedPost,
    handleUpdatePost,
    handleDeletePost,
    handleCreateComment,
  } = usePostContext();

  const { title, author, comments, image, createdAt } = selectedPost || {};

  const isMine = selectedPost?.author?._id === userId;

  const [isVisible, setIsVisible] = useState(false);

  const handleOpen = useCallback(() => {
    setIsVisible(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsVisible(false);
  }, []);

  const onUpdatePost = useCallback(
    async (values) => {
      await handleUpdatePost({
        ...values,
        postId,
      });

      handleClose();
    },
    [handleUpdatePost, postId, handleClose],
  );

  const onCreateComment = useCallback(
    async (values) => {
      const data = { comment: values.title, postId };
      await handleCreateComment(data, author?._id);
    },
    [handleCreateComment, postId, author?._id],
  );

  const onDeletePost = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();

      await handleDeletePost(postId);
    },
    [handleDeletePost, postId],
  );

  useEffect(() => {
    if (postId) {
      handleSetSelectedPost(postId);
    }
  }, [handleSetSelectedPost, postId]);

  return (
    <div>
      <QuestionerContainer>
        <AvatarName
          size="md"
          username={author?.username || ''}
          src="https://picsum.photos/200?random=1"
        />
        <div>{createdAt?.substring(0, 10)}</div>
      </QuestionerContainer>
      <PostContainer>
        {isMine ? (
          <Actions>
            <IconButton
              name="edit"
              onClick={handleOpen}
              size={theme.iconSizes.sm}
              color={theme.colors.gray600}
            />
            <IconButton
              name="trash"
              onClick={onDeletePost}
              size={theme.iconSizes.sm}
              color={theme.colors.gray600}
            />
          </Actions>
        ) : null}
        <Question>
          <Text size="lg">{title}</Text>
        </Question>
        <Divider direction="horizontal" marginSize={theme.spacing.m} />
        <PostForm
          placeholder="답변을 입력해주세요."
          onSubmit={onCreateComment}
          style={{ marginBottom: theme.spacing.s }}
        />
        <Answers>
          {(comments || []).map((comment) => (
            <>
              <AnswerItem
                key={comment._id}
                comment={comment}
                postId={postId}
                src="https://picsum.photos/200"
              />
            </>
          ))}
        </Answers>
      </PostContainer>
      <Modal visible={isVisible} onClose={handleClose}>
        <PostForm
          initialValue={{
            title,
            image,
          }}
          height="200px"
          placeholder="수정할 내용을 입력해주세요."
          onSubmit={onUpdatePost}
        />
      </Modal>
    </div>
  );
};

export default PostDetail;
